//flowdrive - v-0.1 world

// ─── Single-initialization guard + module scope ─────────────────────────────
//
// Everything below runs inside this IIFE. Two reasons, both raised by App Review:
//
//   1. GLOBALS. Unwrapped, this file put 27 bindings on the customer's page —
//      formatSize, parseSize, injectCSS, uploadFile and friends. Ordinary names
//      that a Webflow site is entirely likely to use itself, where a top-level
//      `const` collision is not a shadowing bug but a page-breaking
//      SyntaxError. Nothing here was ever meant to be public API.
//
//   2. DOUBLE INITIALIZATION. A site can end up with two copies of this runtime:
//      a manual <script data-fd-id> pasted into Site Settings, plus the copy our
//      Webflow app applies through the Custom Code API. Both would scan the DOM,
//      both would bind handlers to the same elements, and an upload would fire
//      twice. We cannot remove a customer's manual paste for them — the Custom
//      Code API only exposes scripts applied by apps, never the site's own custom
//      code panel — so the runtime has to tolerate the overlap itself.
//
// FIRST ONE WINS. The manual paste usually sits in Head Code and the API-applied
// copy in the footer, so in a duplicate install the manual copy gets here first
// and keeps control. That is the safe outcome, not the ideal one: the copy still
// running is the unversioned one. Hence the beacon — a duplicate is a migration
// task, and it has to be visible to be actioned.
(function () {
    "use strict";

// Substituted at build time from package.json by scripts/build-widget.mjs,
// exactly like FD_CSS_URL / FD_CSS_SRI. The "dev" fallback applies only to
// unbuilt source loaded directly.
//
// This is read from the build because it ALREADY DRIFTED ONCE AND SHIPPED:
// package.json went to 1.1.8 while this literal stayed "1.1.7", so every site
// running 1.1.8 reported window.__fd.v === "1.1.7" and the duplicate-guard
// warning below named the wrong version — the one message whose entire job is
// telling a site owner which copy to remove (App Review finding #9). Deriving
// it makes that drift impossible instead of merely unlikely.
const WIDGET_VERSION = typeof FD_VERSION !== "undefined" ? FD_VERSION : "dev";

// A duplicate install is surfaced to the SITE OWNER via the console warning below
// (App Review finding #8). We intentionally send NO network telemetry for it — the
// previous fire-and-forget beacon to /widget/duplicate was removed, so nothing about
// the visitor's page leaves the browser here.

// Which copy am I? The reviewed, API-managed runtime is served from a versioned,
// immutable path (/widget/<semver>/…); a hand-pasted manual copy loads from an
// unversioned URL. The two builds are byte-identical, so that <script src> is the
// only runtime-observable difference between them.
const __fdSelf =
    document.currentScript || document.querySelector("script[data-fd-id]");
const __fdManaged = !!(
    __fdSelf && __fdSelf.src && /\/widget\/\d+\.\d+\.\d+\//.test(__fdSelf.src)
);
// A unique per-copy identity, so a copy can tell at init time whether it still
// owns the page or was superseded by a higher-precedence copy after it registered
// its DOMContentLoaded listener (finding #9 — prevents a double init).
const __fdToken = {};

// Version-aware duplicate guard (App Review finding #9). window.__fd carries the
// version (for the console warning) and whether the running copy is the managed,
// versioned one. Precedence: the reviewed versioned copy must win over a mutable
// manual paste — even if the manual copy loaded first — so a migrated site never
// runs the unversioned runtime. We only stand down when we are NOT the copy that
// should win (equal/lower precedence than whoever already claimed the page).
if (window.__fd && window.__fd.v) {
    if (!(__fdManaged && !window.__fd.managed)) {
        console.warn(
            "[flowdrive] widget " + WIDGET_VERSION + " skipped: version " +
            window.__fd.v + " is already running on this page. Remove the duplicate " +
            "<script data-fd-id> tag from your site settings."
        );
        return;
    }
    // We are the reviewed versioned copy and an unversioned copy got here first.
    // Take ownership; the superseded copy yields when it tries to initialize.
    console.warn(
        "[flowdrive] versioned widget " + WIDGET_VERSION +
        " is superseding an unversioned copy already on this page."
    );
}
window.__fd = { v: WIDGET_VERSION, managed: __fdManaged, token: __fdToken };

// Resolve our own <script> tag once, here at the top level. document.currentScript
// is only valid during this file's synchronous execution — read it from a callback
// or after an await and it is null. Two things depend on it: the data-fd-id API key,
// and resolving sibling assets (widget.css) relative to wherever this runtime is
// published.
const scriptTag =
    document.currentScript || document.querySelector("script[data-fd-id]");
const apiKey = scriptTag ? scriptTag.getAttribute("data-fd-id") : "";

// Used only when the script tag has no usable src (inlined, or injected without
// one). The published runtime always resolves widget.css relative to its own URL.
const CSS_FALLBACK_URL = "https://files.tryflowdrive.com/widget.css";

// Build-baked, versioned, SRI-pinned stylesheet reference (App Review finding #7 —
// every customer-site dependency must be immutable, versioned and tamper-evident).
// build-widget.mjs substitutes FD_CSS_URL / FD_CSS_SRI (via esbuild `define`) with
// the exact versioned URL + sha384 of widget.css. `typeof` guards so the raw,
// unbuilt source still runs in local dev (where they're undefined) — it just loads
// the stylesheet relative + unpinned there, which is fine for a dev page.
const WIDGET_CSS_URL = (typeof FD_CSS_URL !== "undefined") ? FD_CSS_URL : null;
const WIDGET_CSS_SRI = (typeof FD_CSS_SRI !== "undefined") ? FD_CSS_SRI : null;


/**
 * The host page's CSP nonce, or "" if it sets none.
 *
 * Read via the `nonce` IDL property rather than getAttribute("nonce"): when the
 * policy arrives as an HTTP header, browsers hide the attribute from the DOM so a
 * CSS attribute selector cannot exfiltrate it. The property is unaffected, so it is
 * the one form that works whether the policy came from a header or a <meta> tag.
 */
function getCspNonce() {
    const el = document.querySelector(
        "script[nonce], style[nonce], link[nonce], meta[property='csp-nonce']"
    );
    return (el && el.nonce) || "";
}

/**
 * Load the widget stylesheet.
 *
 * This used to build a <style> element and pour a template literal from index.js
 * into it. Two problems: a stylesheet generated by JavaScript is not something a
 * reviewer can read, and a strict Content-Security-Policy blocks an injected
 * <style> outright — which left the widget completely unstyled on any site with
 * one. The CSS now lives in widget.css, a static file served beside this runtime.
 *
 * The href is resolved against our own <script src>, so a versioned runtime
 * (e.g. /widget/1.0.0/index.js) automatically loads the stylesheet published
 * alongside it rather than whatever sits at the root.
 */
function injectCSS() {
    // Preferred: the build-baked versioned URL + its SRI hash. This is the only
    // path a published runtime takes — immutable, versioned, and tamper-evident
    // (the browser refuses the stylesheet if its bytes don't match the hash).
    let href = WIDGET_CSS_URL;
    let integrity = WIDGET_CSS_SRI;

    if (!href) {
        // Unbuilt/dev runtime (no baked constants): resolve beside our own
        // <script src>, unpinned. Never reached by a published build.
        integrity = null;
        if (scriptTag && scriptTag.src) {
            try {
                href = new URL("widget.css", scriptTag.src).href;
            } catch {
                // Malformed src — fall back to the canonical URL below.
            }
        }
        if (!href) href = CSS_FALLBACK_URL;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;

    // SRI on a cross-origin stylesheet requires crossorigin; the CDN serves
    // widget.css with `Access-Control-Allow-Origin: *`, so this loads cleanly.
    if (integrity) {
        link.integrity = integrity;
        link.crossOrigin = "anonymous";
    }

    // Sites that allowlist by nonce rather than by host still get styles.
    const nonce = getCspNonce();
    if (nonce) link.nonce = nonce;

    // Idempotent (App Review finding #9). This runs at module scope, outside the
    // duplicate guard, and the guard's precedence rule lets a versioned copy SUPERSEDE
    // an unversioned one — meaning it continues executing rather than returning, so a
    // site with both a manual paste and a managed install loaded two stylesheets.
    //
    // Keyed on our own marker, NOT on href: the two copies do not necessarily resolve
    // the same URL. A published build has WIDGET_CSS_URL baked in and both copies are
    // byte-identical, but the dev fallback resolves widget.css relative to each copy's
    // own <script src>, so an unversioned and a versioned copy compute different hrefs
    // for the same stylesheet. The marker is true regardless of how href was derived.
    if (document.querySelector('link[data-fd-css]')) return;
    link.setAttribute('data-fd-css', WIDGET_VERSION);

    document.head.appendChild(link);
}

injectCSS();

document.addEventListener("DOMContentLoaded", function () {
    // Only the copy that currently owns the page initializes. If a higher-precedence
    // (versioned) copy superseded us after this listener was registered,
    // window.__fd.token no longer matches ours and we yield — no double init (#9).
    if (!window.__fd || window.__fd.token !== __fdToken) return;
    initializeFileUploadWidgets(false);
    watchForNewWidgets();
});

//------------------------------------------------------------------------------------------------

//Flowdrive secure 

/**
 * Flowdrive Secure Files widget — CDN edition.
 *
 * Drop into any page using Memberstack, loaded from any Flowdrive domain
 * (the worker serves this file and /secure/sign on every host):
 *   <script src="https://assets.acme.com/flowdrive-secure.js" defer></script>
 *
 * Mark secured elements with data-fd-secure (or bare fd-secure, matching the
 * upload widget's fd-* attribute convention). One attribute, two modes:
 *
 *   1. Marker mode — attribute empty, URL stays where it always was:
 *        <img src="https://assets.acme.com/KSLBd4a" fd-secure>
 *   2. Deferred mode — URL lives in the attribute; nothing loads (not even
 *      the placeholder) until authorization resolves:
 *        <img fd-secure="https://assets.acme.com/KSLBd4a">
 *
 * Downloads: <a href="..." fd-secure download>Download</a>
 * (any marked element with a `download`, `fd-download`, or
 * `data-fd-download` attribute)
 *
 * For a host the server VOUCHED for, the script only appends query params (exp/sig
 * when authorized, state/type hints for placeholders) and never rewrites the URL —
 * the page's URL is the source of truth, so every shape works: storage paths, file
 * ids, /versions/…, ?thumbnail, custom domains. Unsigned secured URLs already serve
 * the placeholder from the CDN gate, so a forgotten attribute fails visible-closed.
 *
 * For anything the server did NOT vouch for, the URL is REPLACED with placeholder
 * art on a Flowdrive origin. Decorating an unvetted URL would mean the browser
 * requesting a host we don't control (App Review finding #6), so fail-closed here
 * has to mean a URL we own, not a modified version of the page's.
 */
(function () {
    'use strict';

    // Both spellings accepted: bare fd-* matches the upload widget's
    // convention (fd-input, fd-accept…); data-fd-* is the HTML-valid form.
    var ATTRS = ['fd-secure', 'data-fd-secure'];
    var MARK_SELECTOR = '[' + ATTRS.join('],[') + ']';
    var OWN_PARAMS = ['exp', 'sig', 'dl', 'state', 'type']; // params this script manages

    // Boolean-ish values (true/false) are a common authoring mistake: people
    // write data-fd-secure="true" expecting an on/off toggle. The real contract
    // is marker mode (empty) vs deferred mode (the value IS the URL), so "true"
    // would otherwise be read as a relative URL ("…/true"), hiding the element's
    // own src/<source>. Treat these literals as marker mode ('') so resolution
    // falls through to the real URL.
    var BOOLISH_VALUE = /^(?:true|false)$/i;

    // First present attribute wins; '' (marker mode) is a meaningful value,
    // so this distinguishes "attribute absent" (null) from "attribute empty".
    function markAttr(el) {
        for (var i = 0; i < ATTRS.length; i++) {
            var v = el.getAttribute(ATTRS[i]);
            if (v !== null) return BOOLISH_VALUE.test(v.trim()) ? '' : v;
        }
        return null;
    }

    var API_ORIGIN = (function () {
        return 'https://files.tryflowdrive.com';
    })();
    var SIGN_ENDPOINT = API_ORIGIN + '/secure/sign';

    function urlAttrFor(el) {
        return el.tagName === 'A' ? 'href' : 'src';
    }

    function wantsDownload(el) {
        return el.hasAttribute('download') || el.hasAttribute('fd-download') ||
            el.hasAttribute('data-fd-download');
    }

    // The element that actually carries the URL. A marked <video>/<audio> with
    // no src of its own delegates to its first <source> child.
    function urlElementFor(el) {
        if ((el.tagName === 'VIDEO' || el.tagName === 'AUDIO') && !el.getAttribute('src')) {
            var source = el.querySelector('source[src], source' + MARK_SELECTOR.replace(/,/g, ', source'));
            if (source) return source;
        }
        return el;
    }

    // Base URL = deferred-mode attribute value, else current src/href — minus
    // the params we manage, so re-runs (login/logout) are idempotent and never
    // stack stale exp/sig/state params.
    /**
     * True if a secure-media URL is safe to process.
     *
     * This used to be `new URL(raw, location.href)` and nothing else — any scheme,
     * any host. Two concrete problems with that:
     *
     *   javascript: and data: parse fine as URLs. urlAttrFor() returns 'href' for
     *   anchors, so <a fd-secure="javascript:..."> ended up written back to href.
     *
     *   http: and arbitrary hosts meant we would take the path, have Flowdrive sign
     *   it with the visitor's entitlement, and then append that signature to
     *   whatever host was in the attribute — handing a valid signature to it.
     *
     * HTTPS-only kills both, and costs nothing: Flowdrive's CDN is HTTPS, so no
     * legitimate secure-media URL is affected.
     *
     * Host allow-listing is deliberately NOT done here: orgs may serve files from
     * their own custom domains (see flowdrive-cdn worker.ts custom-domain
     * resolution), so a fixed list of Flowdrive hosts would reject legitimate URLs
     * and blank their media.
     */
    function isProcessableMediaUrl(u) {
        if (u.protocol !== 'https:') return false;

        var host = u.hostname.toLowerCase();
        if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return false;  // IPv4 literal
        if (host.indexOf(':') !== -1) return false;              // IPv6 literal
        if (host === 'localhost' || /\.localhost$/.test(host)) return false;

        return true;
    }

    function baseUrlFor(markedEl, urlEl) {
        var raw = markAttr(markedEl) || markAttr(urlEl) ||
            urlEl.getAttribute(urlAttrFor(urlEl));
        if (!raw) return null;
        try {
            var u = new URL(raw, window.location.href);

            // Fail closed: an unprocessable URL is skipped entirely rather than
            // signed. The site owner gets a console error naming the URL, since a
            // silently-skipped element is otherwise indistinguishable from a bug.
            if (!isProcessableMediaUrl(u)) {
                console.error(
                    'Flowdrive: refusing to process secure media URL "' + raw +
                    '" — only https:// URLs are supported (no javascript:, data:, ' +
                    'http:, IP literals or localhost).'
                );
                return null;
            }

            OWN_PARAMS.forEach(function (p) { u.searchParams.delete(p); });
            return u;
        } catch (e) {
            return null;
        }
    }

    // Path sent to /secure/sign: decoded pathname, no leading slash. The query
    // (?thumbnail etc.) stays on the URL but is not part of the signed path.
    function pathFor(baseUrl) {
        var p = baseUrl.pathname.replace(/^\/+/, '');
        try { return decodeURIComponent(p); } catch (e) { return p; }
    }

    function collectTargets() {
        var targets = [];
        document.querySelectorAll(MARK_SELECTOR).forEach(function (el) {
            var urlEl = urlElementFor(el);
            var base = baseUrlFor(el, urlEl);
            if (!base) return; // no URL anywhere → nothing to manage
            var path = pathFor(base);
            if (!path) return;
            targets.push({ el: el, urlEl: urlEl, base: base, path: path, download: wantsDownload(el) });
        });
        return targets;
    }

    // Set the URL only when it actually changed — re-setting an identical src
    // can still restart media loads in some browsers.
    function setUrl(urlEl, urlStr) {
        var attr = urlAttrFor(urlEl);
        if (urlEl.getAttribute(attr) === urlStr) return;
        urlEl.setAttribute(attr, urlStr);
        if (urlEl.tagName === 'SOURCE' && urlEl.parentElement && typeof urlEl.parentElement.load === 'function') {
            urlEl.parentElement.load();
        } else if ((urlEl.tagName === 'VIDEO' || urlEl.tagName === 'AUDIO') && typeof urlEl.load === 'function') {
            urlEl.load();
        }
    }

    // Storage paths are often extensionless nanoids, so the server can't tell
    // image/video/pdf apart for placeholder art. The widget knows the element.
    function elementType(urlEl) {
        var tag = urlEl.tagName;
        if (tag === 'IMG') return 'image';
        if (tag === 'VIDEO' || tag === 'SOURCE' || tag === 'AUDIO') return 'video';
        if (tag === 'EMBED' || tag === 'IFRAME' || tag === 'OBJECT') return 'pdf';
        return ''; // <a>/download → generic HTML placeholder
    }

    function applySigned(t, params) {
        var u = new URL(t.base.toString());
        u.searchParams.set('exp', params.exp);
        u.searchParams.set('sig', params.sig);
        if (t.download) u.searchParams.set('dl', '1');
        setUrl(t.urlEl, u.toString());
    }

    // Unsigned URL → the CDN gate serves the placeholder. We add hints:
    //   state=denied → "no access" art (logged in, lacks the plan)
    //   type=…       → art matched to the element kind
    //
    // ONLY safe for a host the server vouched for (approved[]). This decorates the
    // URL the PAGE supplied rather than replacing it — which is deliberate, because
    // orgs serve files from their own custom domains and the gate on that domain
    // serves the art. On a host we do not own the very same code becomes a request
    // to someone else's server with our params attached, so callers must route
    // unvouched targets to applyInert() instead.
    function applyPlaceholder(t, denied) {
        var u = new URL(t.base.toString());
        if (denied) u.searchParams.set('state', 'denied');
        var type = elementType(t.urlEl);
        if (type) u.searchParams.set('type', type);
        setUrl(t.urlEl, u.toString());
    }

    // Placeholder art on OUR OWN origin, ignoring the page-supplied URL entirely.
    // This is what "fail closed" has to mean in a browser: not a modified version
    // of the untrusted URL, but a URL we control. Extensionless for the generic
    // case — Workers Assets 307s /secure-placeholder.html to /secure-placeholder.
    var INERT_EXT = { image: '.svg', video: '.mp4', pdf: '.pdf' };

    function applyInert(t, denied) {
        var ext = INERT_EXT[elementType(t.urlEl)] || '';
        setUrl(t.urlEl, API_ORIGIN + '/secure-placeholder' + (denied ? '-denied' : '') + ext);
    }

    function applyInertAll(targets, denied) {
        targets.forEach(function (t) { applyInert(t, denied); });
    }

    // Returns { params: {path: {exp, sig}}, denied: [path], approved: [path],
    //           authenticated: bool }.
    // Keys are the paths exactly as sent.
    //
    // approved[] is the origin gate's positive verdict: the server resolved the
    // origin we sent for that path against Flowdrive's hosts and the org's custom
    // domains, and it is one of ours. A path missing from approved[] was NEVER
    // vouched for and its URL must not be written back to the DOM, whatever else
    // the response says. A path in approved[] but absent from params and denied is
    // a genuine public file and serves normally without a signature.
    function fetchSign(targets, memberToken, download, mode) {
        if (targets.length === 0) return Promise.resolve({ params: {}, denied: [], authenticated: false });
        var paths = targets.map(function (t) { return t.path; });

        // Send the ORIGIN each path came from, positionally paired with paths[].
        //
        // pathFor() throws the host away — it has to, because the signature is
        // over the file's storage path and must stay valid across every domain
        // that resolves to the file. But that left the server signing a path
        // with no idea which host the signature was about to be pinned onto, so
        // a URL pointing anywhere at all got a signature minted against the
        // visitor's real entitlement and then handed to that host.
        //
        // The server is the only side that can settle this: deciding whether a
        // host is ours means checking the org's custom domains, which lives in
        // the database. So it gets the origins and refuses to sign the ones that
        // aren't ours. Sending them rather than validating here is also what
        // makes the check unbypassable — a page that tampers with this runtime
        // still cannot talk the server into signing a foreign host.
        var origins = targets.map(function (t) {
            try { return t.base.origin; } catch (e) { return ''; }
        });

        // Deliberately NO Content-Type header: a JSON content-type makes this a
        // CORS "preflighted" request, costing an extra OPTIONS round-trip per
        // page view. Without it the browser sends text/plain (a "simple
        // request", zero preflight) and the server parses the JSON regardless.
        return fetch(SIGN_ENDPOINT, {
            method: 'POST',
            body: JSON.stringify({ paths: paths, origins: origins, memberToken: memberToken, download: download, mode: mode })
        }).then(function (res) {
            if (!res.ok) throw new Error('sign failed: ' + res.status);
            return res.json();
        }).then(function (data) {
            // No approved[] in the response → vouch for nothing. A response shape we
            // don't recognise must not be read as blanket permission.
            return {
                params: data.params || {},
                denied: data.denied || [],
                approved: Array.isArray(data.approved) ? data.approved : [],
                authenticated: !!data.authenticated
            };
        });
    }

    // The host check comes FIRST, before signed/denied/public, because it decides a
    // different question: not "may this visitor see the file" but "may we point the
    // browser at this URL at all". Getting that order wrong is what made the denial
    // path fail open — the runtime reached for placeholder art and, in doing so,
    // requested the attacker's host.
    function applyResult(group, result) {
        group.forEach(function (t) {
            var vouched = result.approved.indexOf(t.path) !== -1;

            if (result.params[t.path]) {
                // Signed. The server only signs origins it owns, so this is vouched
                // by construction.
                applySigned(t, result.params[t.path]);
            } else if (!vouched) {
                // Unvouched host: foreign, undisclosed, or a response we can't read.
                // Never write the page-supplied URL — swap in art from our origin.
                //
                // Say so. isProcessableMediaUrl above console.errors on exactly the
                // stated grounds that "a silently-skipped element is otherwise
                // indistinguishable from a bug", and this branch broke that rule:
                // a customer serving Flowdrive media through their own UNREGISTERED
                // host got blank media and no diagnostic anywhere. Naming the host
                // is the difference between "Flowdrive is broken" and "add this
                // domain in Flowdrive". Fires per unvouched element, which is the
                // same cardinality the sibling check already accepts.
                console.error(
                    'Flowdrive: not serving secure media from "' + t.base.host +
                    '" — that host is not registered to your Flowdrive organization, ' +
                    'so a placeholder is shown instead. Add it under Settings → Domains.'
                );
                applyInert(t, result.authenticated);
            } else if (result.denied.indexOf(t.path) !== -1) {
                // Ours, but this visitor may not see it → gate serves the art.
                applyPlaceholder(t, result.authenticated);
            } else {
                // Public file on a host we vouched for — plain base URL serves normally.
                setUrl(t.urlEl, t.base.toString());
            }
        });
    }

    function waitForMemberstack(attempts) {
        return new Promise(function (resolve) {
            (function poll(left) {
                if (window.$memberstackDom) return resolve(window.$memberstackDom);
                if (left <= 0) return resolve(null);
                setTimeout(function () { poll(left - 1); }, 250);
            })(attempts);
        });
    }

    function run() {
        // Same ownership check the upload widget uses (App Review finding #9). Both
        // copies on a duplicated page register this listener, but only the copy that
        // still owns window.__fd.token may act — otherwise a manual paste plus a
        // managed install produced TWO signing flows for every marked element. The
        // check is here rather than at registration time because ownership can change
        // after a copy registers: the versioned copy supersedes an unversioned one.
        if (!window.__fd || window.__fd.token !== __fdToken) return;

        var targets = collectTargets();
        if (targets.length === 0) return;

        // Resolve member token + Memberstack mode if available — but always sign,
        // even logged out, so PUBLIC files load. The server signs what the
        // requester may access and denies the rest, per file.
        waitForMemberstack(40).then(function (ms) {
            var tokenP = ms ? Promise.resolve(ms.getMemberCookie()).catch(function () { return null; }) : Promise.resolve(null);
            var modeP = ms ? Promise.resolve(ms.getApp()).then(function (a) { return a && a.data && a.data.mode; }).catch(function () { return undefined; }) : Promise.resolve(undefined);

            Promise.all([tokenP, modeP]).then(function (resolved) {
                var memberToken = resolved[0];
                var mode = resolved[1];

                var viewTargets = targets.filter(function (t) { return !t.download; });
                var dlTargets = targets.filter(function (t) { return t.download; });

                Promise.all([
                    fetchSign(viewTargets, memberToken, false, mode),
                    fetchSign(dlTargets, memberToken, true, mode)
                ]).then(function (results) {
                    applyResult(viewTargets, results[0]);
                    applyResult(dlTargets, results[1]);
                }).catch(function (err) {
                    console.warn('[flowdrive-secure] could not sign URLs:', err);
                    // A 500 or a dropped connection means we hold NO verdict for any
                    // target — including which hosts are ours. This used to revert
                    // every element to its base URL, so one failed request fixed the
                    // whole page open. Fail closed on all of them instead: a transient
                    // outage showing placeholder art is recoverable, a request to an
                    // unvetted host is not.
                    applyInertAll(targets, !!memberToken);
                });
            });
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run);
    } else {
        run();
    }

    // Re-run on any auth change. On logout we re-run (not blanket-placeholder)
    // so PUBLIC files stay visible and only gated ones revert to the login prompt.
    window.addEventListener('memberstack:login', run);
    window.addEventListener('memberstack:signup', run);
    window.addEventListener('memberstack:logout', run);
})();

//------------------------------------------------------------------------------------------------


// A <div> rendered by a framework (React, Svelte, Vue…) belongs to that framework:
// replacing it breaks the framework's next update or unmount ("removeChild: not a
// child", or an orphaned uploader left on screen). Those divs get the uploader mounted
// INSIDE instead. Everything else keeps the original replace behaviour, because the
// replacement inherits the div's class and existing Webflow/HTML layouts depend on it.
//
// "Framework-owned" is: appeared after load (afterLoad), opted in with
// fd-mount="inside", or carrying a framework's own node markers. Note that load time
// alone can't decide it: an app's module scripts render BEFORE DOMContentLoaded, so a
// React or Svelte page has its div in place by then. Only React tags its nodes in
// production builds (__reactFiber$…); Vue's __vnode / __vueParentComponent are
// dev-only, and Svelte sets nothing, so the install snippets for Svelte and Vue
// include fd-mount="inside". The Vue/Angular checks below just catch dev builds.
function ownedByFramework(el) {
    const keys = Object.getOwnPropertyNames(el);
    for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (k.startsWith("__react") || k === "__vueParentComponent" || k === "__vnode" || k === "__ngContext__") {
            return true;
        }
    }
    return false;
}

// Marks the page once at least one upload widget is set up and accepting files.
// App Review Preflight's browser test waits for this selector ([data-fd-ready])
// before it records what the runtime loaded.
function markReady() {
    document.documentElement.setAttribute("data-fd-ready", "");
}

function initializeFileUploadWidgets(afterLoad) {
    let initialized = 0;
    const uploadElements = document.querySelectorAll("[fd-upload='default']");
    uploadElements.forEach((element) => {
        // Idempotent: a second init pass (e.g. a stale non-yielding copy racing the
        // current one) must not convert the same element twice (finding #9).
        if (element.__fdInitialized) return;
        if (element.tagName === "DIV") {
            element.__fdInitialized = true;
            const inside =
                !!afterLoad ||
                element.getAttribute("fd-mount") === "inside" ||
                ownedByFramework(element);
            replaceDivWithCustomUpload(element, inside);
            initialized++;
        }
    });

    // Initialize custom upload widgets
    const customUploadElements = document.querySelectorAll(
        "[fd-upload='custom']"
    );
    customUploadElements.forEach((element) => {
        // Idempotent: setting up an already-initialized widget re-clones a consumed
        // template (throws) and double-binds change. Mark once, skip thereafter (#9).
        if (element.__fdInitialized) return;
        element.__fdInitialized = true;
        setupCustomUploadWidget(element);
        initialized++;
    });

    if (initialized > 0) markReady();
}

// Single-page apps render the widget's <div> whenever a route, modal or form step
// shows it, long after DOMContentLoaded. Watch for [fd-upload] elements being added
// and set them up then. Init is idempotent (__fdInitialized), so a rescan is safe.
function watchForNewWidgets() {
    if (typeof MutationObserver === "undefined") return;
    let queued = false;
    const hasWidget = (node) =>
        node.nodeType === 1 &&
        ((node.matches && node.matches("[fd-upload]")) ||
            (node.querySelector && node.querySelector("[fd-upload]")));
    new MutationObserver((records) => {
        if (queued) return;
        // A superseding copy owns the page now; it runs its own observer (#9).
        if (!window.__fd || window.__fd.token !== __fdToken) return;
        const added = records.some((r) => Array.prototype.some.call(r.addedNodes, hasWidget));
        if (!added) return;
        // One rescan per burst of DOM changes, after the framework finishes rendering.
        queued = true;
        Promise.resolve().then(() => {
            queued = false;
            initializeFileUploadWidgets(true);
        });
    }).observe(document.documentElement, { childList: true, subtree: true });
}

const FILE_TYPE_CATEGORIES = {
    image: [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/svg+xml",
        "image/webp",
        "image/gif",
        "image/avif",
        "image/x-icon",
        "image/apng",
    ],
    video: [
        "video/mp4",
        "video/mpeg",
        "video/3gpp",
        "video/x-msvideo",
        "video/x-matroska",
        "video/ogg",
        "video/quicktime",
        "video/webm",
    ],
    audio: [
        "audio/mpeg",
        "audio/aac",
        "audio/ogg",
        "audio/webm",
        "audio/wav",
        "audio/3gpp",
    ],
    code: [
        "text/plain",
        "text/html",
        "text/css",
        "text/csv",
        "text/javascript",
        "application/javascript",
        "application/json",
        "application/xhtml+xml",
    ],
    docs: [
        "application/pdf",
        "application/epub+zip",
    ]
};

function setupCustomUploadWidget(element) {
    const inputElement = element.querySelector('input[type="file"]');
    const urlInputElement = document.querySelector(element.getAttribute("fd-input"));
    if (urlInputElement) canWriteTarget(urlInputElement); // warn at load, not first upload (#6)
    const triggerButton = element.querySelector('[fd-upload="trigger"]');
    const previewContainerId = element.getAttribute("fd-show-preview");
    const previewContainer = previewContainerId ? document.querySelector(`[fd-preview-list="${previewContainerId}"]`) : null;
    const isMultiple = inputElement.hasAttribute("multiple");
    const maxSizeAttr = element.getAttribute("fd-max-size");
    const acceptAttr = element.getAttribute("fd-accept");
    const isPrepend = previewContainer ? previewContainer.hasAttribute("fd-prepend") : null;
    const folderId = element.getAttribute("fd-folder");

    if (!inputElement || !urlInputElement || !triggerButton) {
        console.error("Custom upload widget is missing required elements.");
        return;
    }

    const maxSize = maxSizeAttr ? convertSizeToBytes(maxSizeAttr) : null;
    const acceptedTypes = acceptAttr ? getAcceptedTypes(acceptAttr) : inputElement.accept ? inputElement.accept.split(",") : null;

    // Store the original item template if preview container exists
    const itemTemplate = previewContainer ? previewContainer.querySelector('[fd-preview="item"]').cloneNode(true) : null;
    if (previewContainer) {
        clearPreviewContainer(previewContainer, isPrepend); // Clear preview container based on the isPrepend flag
    }

    triggerButton.addEventListener("click", () => inputElement.click());

    inputElement.addEventListener("change", (event) => {
        const files = event.target.files;
        if (files.length > 0) {
            if (!isMultiple && previewContainer) {
                clearPreviewContainer(previewContainer, isPrepend); // Clear previous previews for single file input
            }
            // Errors are per-selection: clear the last batch's before validating this one.
            clearWidgetErrors(element);
            Array.from(files).forEach(file => {
                // Check file size
                if (maxSize && file.size > maxSize) {
                    showWidgetError(element, file.name, `Larger than the ${maxSizeAttr} limit.`);
                    return;
                }
                if (acceptedTypes && !fileMatchesAccept(file, acceptedTypes)) {
                    showWidgetError(element, file.name, `This file type isn't accepted.`);
                    return;
                }

                const fileId = `file-${Math.random().toString(36).substr(2, 9)}`;
                if (previewContainer && itemTemplate) {
                    const itemClone = displayFilePreview(file, previewContainer, itemTemplate, isMultiple, fileId, urlInputElement, isPrepend);
                    uploadFile(file, folderId, (progress) => {
                        // Update progress in preview
                        if (previewContainer) {
                            const progressFill = itemClone.querySelector(`[fd-preview="progress-fill"]`);
                            const progressNumber = itemClone.querySelector(`[fd-preview="progress-number"]`);
                            if (progressFill) {
                                progressFill.style.width = `${progress}%`;
                            }
                            if (progressNumber) {
                                progressNumber.textContent = `${progress}%`;
                            }
                        }
                    }, (success, fileUrl, error) => {
                        if (success) {
                            // Update the input with the file URL or perform necessary action
                            updateHiddenInput(urlInputElement, fileUrl, isMultiple); // Use the extracted URL
                            itemClone.dataset.fileUrl = fileUrl; // Assign fileUrl to the item clone
                            handleSuccessElements(itemClone, "success");
                            emitWidgetEvent(element, "fdupload", { url: fileUrl, name: file.name, size: file.size, type: file.type });
                        } else {
                            // A cancelled upload is the visitor's own doing, not a
                            // failure — the row is already being removed, so painting
                            // a "fail" state onto it is both wrong and racy.
                            if (error && error.code === "CANCELLED") return;
                            console.error("Upload failed for", file.name);
                            handleSuccessElements(itemClone, "fail");
                            emitWidgetEvent(element, "fderror", { name: file.name, code: (error && error.code) || null, message: uploadErrorMessage(error) });
                        }
                    }).catch((error) => {
                        // uploadFile() rethrows after onComplete so callers can tell
                        // cancellation from failure. Without this, every cancel and
                        // every network/HTTP error on this path became an unhandled
                        // promise rejection (App Review finding 7a). The UI half is
                        // already done by onComplete above; this only absorbs.
                        if (error && error.code === "CANCELLED") return;
                    });
                } else {
                    uploadFile(file, folderId, (progress) => { }, (success, fileUrl, error) => {
                        if (success) {
                            // Update the input with the file URL or perform necessary action
                            updateHiddenInput(urlInputElement, fileUrl, isMultiple); // Use the extracted URL
                            emitWidgetEvent(element, "fdupload", { url: fileUrl, name: file.name, size: file.size, type: file.type });
                        } else {
                            if (error && error.code === "CANCELLED") return;
                            console.error("Upload failed for", file.name);
                            emitWidgetEvent(element, "fderror", { name: file.name, code: (error && error.code) || null, message: uploadErrorMessage(error) });
                        }
                    }).catch((error) => {
                        // See the sibling call site above: uploadFile() rethrows, and
                        // this path had no handler either (App Review finding 7a).
                        if (error && error.code === "CANCELLED") return;
                    });
                }
            });
            // Reset the file input so selecting the SAME file again still fires
            // 'change'. A file input only fires 'change' when the value differs, so
            // after the visitor cancels an upload (the ✕) and re-picks that same file,
            // nothing happened — the widget looked dead. Clearing the value here lets
            // the re-selection register. (Path 2's cancel handler already does this at
            // ~index.js:1376; this brings the preview-based path in line.)
            inputElement.value = "";
        }
    });
}

function clearPreviewContainer(container, isPrepend) {
    if (isPrepend) {
        const items = container.querySelectorAll('[fd-preview="item"]');
        items.forEach(item => item.remove());
    } else {
        // replaceChildren() rather than innerHTML = '' — clearing this way is equally
        // safe, and it leaves the runtime with zero innerHTML assignments, which is
        // trivially greppable for review.
        container.replaceChildren();
        container.style.display = 'none';
    }
}

function displayFilePreview(file, previewContainer, itemTemplate, isMultiple, fileId, urlInputElement, isPrepend) {
    const itemClone = itemTemplate.cloneNode(true);
    const imgElement = itemClone.querySelector('[fd-preview="img"]');
    const nameElement = itemClone.querySelector('[fd-preview="name"]');
    const sizeElement = itemClone.querySelector('[fd-preview="size"]');
    const progressFill = itemClone.querySelector('[fd-preview="progress-fill"]');
    const progressNumber = itemClone.querySelector('[fd-preview="progress-number"]');
    const cancelElement = itemClone.querySelector('[fd-preview="cancel"]');
    const iconElement = itemClone.querySelector('[fd-preview="icon-wrapper"]');
    const newDisplay = previewContainer.getAttribute("fd-display");

    if (imgElement) {
        if (file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (e) => {
                imgElement.src = e.target.result;
            };
            reader.readAsDataURL(file); // Only read image files as Data URL
        } else {
            // getFileTypeIcon returns a node; replaceChildren swaps it in without
            // going through innerHTML.
            if (!iconElement) {
                const iconWrapper = document.createElement('div');
                iconWrapper.setAttribute('fd-preview', 'icon-wrapper');
                // Insert the icon wrapper where the img element was
                imgElement.parentElement.appendChild(iconWrapper);
                iconWrapper.replaceChildren(getFileTypeIcon(file));
            } else {
                iconElement.replaceChildren(getFileTypeIcon(file));
            }

            imgElement.remove();
        }
    }

    if (nameElement) {
        nameElement.textContent = file.name;
    }

    if (sizeElement) {
        sizeElement.textContent = formatSize(file.size);
    }

    if (progressFill) {
        progressFill.setAttribute("data-file-id", fileId);
    }

    if (progressNumber) {
        progressNumber.setAttribute("data-file-id", fileId);
    }


    if (cancelElement) {
        cancelElement.addEventListener("click", () => {
            // Abort EVERY in-flight request (multipart runs several at once) and clean
            // up the partial upload server-side.
            if (typeof file.abortAll === 'function') file.abortAll();
            if (isMultiple) {
                const fileUrlToRemove = itemClone.dataset.fileUrl;

                if (fileUrlToRemove) {
                    // Assuming hiddenInput is properly scoped to be accessible here
                    updateHiddenInput(
                        urlInputElement,
                        fileUrlToRemove,
                        isMultiple,
                        "remove"
                    ); // Remove the URL from the hidden input for multiple
                }
            } else {
                // For single file uploads, simply clear the hidden input
                if (urlInputElement) {
                    setInputValue(urlInputElement, ""); // Clear the value for single file uploads
                }
            }
            itemClone.remove();
            if (!previewContainer.children.length) {
                previewContainer.style.display = 'none';
            }
        });
    }

    if (isMultiple) {
        if (isPrepend) {
            const nonPreviewItems = Array.from(previewContainer.children).filter(child => !child.hasAttribute('fd-preview'));
            if (nonPreviewItems.length > 0) {
                previewContainer.insertBefore(itemClone, nonPreviewItems[0]);
            } else {
                previewContainer.appendChild(itemClone);
            }
        } else {
            previewContainer.appendChild(itemClone);
        }
    } else {
        clearPreviewContainer(previewContainer, isPrepend); // Clear previous preview for single file input
        previewContainer.appendChild(itemClone);
    }

    if (newDisplay) {
        previewContainer.style.display = `${newDisplay}`; // Show the preview container
    } else {
        previewContainer.style.display = "block";
    }


    return itemClone;
}

function handleSuccessElements(itemClone, status = "success") {
    const hideOnCompleteElements = itemClone.querySelectorAll('[fd-hide-on-complete]');
    const showOnCompleteElements = itemClone.querySelectorAll('[fd-show-on-complete]');
    const hideOnFailedElements = itemClone.querySelectorAll('[fd-hide-on-fail]');
    const showOnFailedElements = itemClone.querySelectorAll('[fd-show-on-fail]');
    const newDisplay = itemClone.getAttribute("fd-display");

    if (status === "success") {
        hideOnCompleteElements.forEach(element => {
            element.style.display = 'none';
        });

        showOnCompleteElements.forEach(element => {
            if (newDisplay) {
                element.style.display = `${newDisplay}`; // Show the preview container
            } else {
                element.style.display = "block";
            }
        });
    } else if (status === "fail") {
        hideOnFailedElements.forEach(element => {
            element.style.display = 'none';
        });

        showOnFailedElements.forEach(element => {
            if (newDisplay) {
                element.style.display = `${newDisplay}`; // Show the preview container
            } else {
                element.style.display = "block";
            }
        });
    }
}

function getAcceptedTypes(acceptAttr) {
    const categories = acceptAttr.split(",").map(type => type.trim());
    let types = [];
    categories.forEach(category => {
        if (FILE_TYPE_CATEGORIES[category]) {
            types = types.concat(FILE_TYPE_CATEGORIES[category]);
        } else {
            types.push(category);
        }
    });
    return types;
}

// Match a file against accepted types, supporting the standard `accept` forms:
// wildcard MIME ("image/*"), extension (".pdf"), and exact MIME ("application/pdf").
// The previous exact `includes(file.type)` check rejected everything a wildcard
// should allow — a PNG's type is "image/png", never the literal "image/*".
function fileMatchesAccept(file, acceptedTypes) {
    const mime = (file.type || "").toLowerCase();
    const name = (file.name || "").toLowerCase();
    return acceptedTypes.some((raw) => {
        const t = String(raw).trim().toLowerCase();
        if (!t) return false;
        if (t === "*" || t === "*/*") return true;
        if (t.endsWith("/*")) return mime.startsWith(t.slice(0, -1)); // "image/" prefix
        if (t.startsWith(".")) return name.endsWith(t);
        return mime === t;
    });
}

function convertSizeToBytes(sizeStr) {
    const sizeUnits = { "B": 1, "KB": 1024, "MB": 1024 ** 2, "GB": 1024 ** 3 };
    const match = sizeStr.match(/(\d+)([BKMG]?B)/i);
    if (!match) {
        // A malformed `fd-max-size` is the site owner's typo, not something the
        // visitor can act on — this used to alert() every visitor about it. Console,
        // matching how the widget already reports missing elements. Returning null
        // means "no limit", which is the pre-existing behaviour.
        console.error(
            `Flowdrive: invalid fd-max-size value "${sizeStr}". Expected a number followed by B, KB, MB or GB (e.g. "10MB"). No size limit will be enforced.`
        );
        return null;
    }

    const size = parseInt(match[1], 10);
    const unit = match[2].toUpperCase();
    return size * sizeUnits[unit];
}

function replaceDivWithCustomUpload(divElement, inside) {
    const isMultiple = divElement.hasAttribute("fd-multiple");
    const inputSelector = divElement.getAttribute("fd-input"); // Check for fd-input attribute
    const fileType = divElement.getAttribute("fd-type"); // New: Get fd-file-type attribute
    const isRequired = divElement.hasAttribute("fd-required");
    const uploadText = divElement.getAttribute("fd-upload-text"); // New attribute for custom upload text
    let inputElement;

    // New: Define file type mappings
    const fileTypeMappings = {
        pdf: ".pdf",
        audio: "audio/*",
        video: "video/*",
        image: "image/*",
        document: ".doc,.docx,.ppt,.pptx,.xls,.xlsx,.pdf", // Example, adjust as needed
    };

    // New: Determine the accept value based on fd-type
    const acceptValue = fileTypeMappings[fileType] || ""; // Default to empty, allowing any file type if unspecified

    // Create the new fdx-container element
    const fdxContainer = document.createElement("fdx-container");

    // Transfer all attributes from the div to the fdx-container, excluding fd-upload.
    // Mounted inside, the div keeps its own class/id/style, so only the fd-* settings
    // move across (copying the class too would apply the customer's styles twice).
    Array.from(divElement.attributes).forEach((attr) => {
        if (attr.name === "fd-upload") return;
        if (inside && !attr.name.startsWith("fd-")) return;
        fdxContainer.setAttribute(attr.name, attr.value);
    });

    if (inputSelector) {
        if (
            inputSelector.startsWith(".") ||
            inputSelector.startsWith("#") ||
            inputSelector.startsWith("[")
        ) {
            // It's a selector, try to find the element in the document
            inputElement = document.querySelector(inputSelector);
            if (!inputElement) {
                console.error(
                    "No element found for the provided fd-input selector:",
                    inputSelector
                );
                // You might want to handle this case, e.g., by creating a fallback input or displaying an error
            }
        } else {
            // It's not a selector: create a hidden input with that name (finding #6:
            // never one that duplicates an existing field or has a sensitive name).
            inputElement = createOwnedField(inputSelector, divElement.closest("form"));
            if (inputElement) fdxContainer.appendChild(inputElement);
        }
    }

    // Create the fdx-upload and input elements
    const fdxUpload = document.createElement("fdx-upload");
    fdxUpload.setAttribute("role", "button"); // Explicitly define role if not using <button>
    fdxUpload.setAttribute("tabindex", "0"); // Ensure focusability
    fdxUpload.setAttribute("aria-label", "Upload files"); // Provide accessible name
    fdxUpload.textContent = uploadText || "Upload files";
    fdxContainer.appendChild(fdxUpload);

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    // Instead of display: none, we position it over the fdx-upload but make it invisible
    fileInput.style.cssText = `
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
  `;
    if (isMultiple) {
        fileInput.setAttribute("multiple", "");
    }
    if (isRequired) {
        fileInput.required = true;
    }
    // New: Set the accept attribute based on fd-file-type
    if (acceptValue) {
        fileInput.setAttribute("accept", acceptValue);
    }
    // Make fdx-upload a relative positioning context for the input
    fdxContainer.style.position = "relative";
    // Ensure the fdx-upload can receive focus for accessibility
    fdxContainer.setAttribute("tabindex", "0");
    fdxContainer.appendChild(fileInput);

    if (inside) {
        // Leave the framework's div where it is and mount inside it
        divElement.appendChild(fdxContainer);
    } else {
        // Replace the div with fdx-container
        divElement.parentNode.replaceChild(fdxContainer, divElement);
    }

    // Set up event listeners, adjust handleFileSelection as needed
    fdxUpload.addEventListener("click", () => fileInput.click());
    fileInput.addEventListener("change", (event) => {
        handleFileSelection(
            event.target.files,
            fdxContainer,
            isMultiple,
            inputElement
        );
    });

    // Add drag and drop listeners
    fdxUpload.addEventListener("dragover", (event) => {
        event.preventDefault();
        fdxUpload.setAttribute("fdx-dragover", "");
    });
    fdxUpload.addEventListener("dragleave", (event) => {
        fdxUpload.removeAttribute("fdx-dragover");
    });
    fdxUpload.addEventListener("drop", (event) => {
        event.preventDefault();
        fdxUpload.removeAttribute("fdx-dragover");
        const files = event.dataTransfer.files;
        handleFileSelection(files, fdxContainer, isMultiple, inputElement);
    });
}

// Calculate file size in a readable format
function formatSize(sizeInBytes) {
    const KB = 1024;
    const MB = KB * 1024;
    const GB = MB * 1024;

    if (sizeInBytes < KB) {
        return `${sizeInBytes} bytes`;
    } else if (sizeInBytes < MB) {
        return `${(sizeInBytes / KB).toFixed(2)} KB`;
    } else if (sizeInBytes < GB) {
        return `${(sizeInBytes / MB).toFixed(2)} MB`;
    } else {
        return `${(sizeInBytes / GB).toFixed(2)} GB`;
    }
}

function getFileTypeIcon(file) {
    const imageContentTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/svg+xml",
        "image/webp",
        "image/gif",
        "image/avif",
        "image/x-icon",
        "image/apng",
    ];
    const videoContentTypes = [
        "video/mp4",
        "video/mpeg",
        "video/3gpp",
        "video/x-msvideo",
        "video/x-matroska",
        "video/ogg",
        "video/quicktime",
        "video/webm",
    ];
    const audioContentTypes = [
        "audio/mpeg",
        "audio/aac",
        "audio/ogg",
        "audio/webm",
        "audio/wav",
        "audio/3gpp",
    ];
    const pdfContentTypes = [
        "application/pdf",
        "application/epub+zip"
    ];
    const codeContentTypes = [
        "text/plain",
        "text/html",
        "text/css",
        "text/csv",
        "text/javascript",
        "application/javascript",
        "application/json",
        "application/xhtml+xml",
    ];

    // Images get a real <img> built with property assignment. This used to be an
    // interpolated HTML string — `alt="${file.name}"` — handed to innerHTML by all
    // three callers, so a file named  x" onerror="...  broke out of the attribute and
    // ran on the customer's page as soon as it was selected, before any upload.
    // Properties are not parsed as markup, so there is nothing to break out of.
    if (imageContentTypes.includes(file.type)) {
        const img = document.createElement("img");
        img.src = URL.createObjectURL(file);
        img.alt = file.name;
        img.className = "file-preview-img";
        img.loading = "lazy";
        return img;
    }

    // Everything below is a fixed, developer-authored SVG with no interpolation.
    let fileTypeHTML;
    if (videoContentTypes.includes(file.type)) {
        fileTypeHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M3.21899 4.2539C3.24657 4.03449 3.32932 3.82408 3.46063 3.63944C3.59194 3.4548 3.76818 3.30105 3.97528 3.19046C4.18238 3.07986 4.41461 3.01549 4.65345 3.00247C4.89228 2.98944 5.13111 3.02814 5.35087 3.11546C5.97739 3.36334 7.30003 3.92109 8.97571 4.81394C10.1529 5.43603 11.2901 6.12039 12.3818 6.8636C12.5736 6.99538 12.7294 7.16675 12.8368 7.36401C12.9441 7.56128 13 7.77898 13 7.99974C13 8.2205 12.9441 8.4382 12.8368 8.63547C12.7294 8.83274 12.5736 9.0041 12.3818 9.13589C11.2901 9.87837 10.1528 10.562 8.97571 11.1832C7.80393 11.8142 6.59363 12.382 5.35087 12.884C5.13113 12.9716 4.89225 13.0105 4.65331 12.9976C4.41438 12.9847 4.18204 12.9203 3.97486 12.8097C3.76768 12.6991 3.59142 12.5452 3.46017 12.3604C3.32892 12.1757 3.24632 11.9651 3.21899 11.7456C3.06867 10.5018 2.99563 9.25119 3.0002 7.99974C3.0002 6.21978 3.13943 4.87935 3.21899 4.2539Z" fill="#303030"/>
    </svg>`;
    } else if (audioContentTypes.includes(file.type)) {
        fileTypeHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M5.99333 3.34407L13.34 2.00538C13.7067 1.95184 14 2.30659 14 2.67473V10.988C14 12.2865 12.9533 13.3307 11.6667 13.3307C10.38 13.3307 9.33333 12.2865 9.33333 10.988C9.33333 9.69613 10.38 8.64526 11.6667 8.64526C12.0267 8.64526 12.36 8.73897 12.6667 8.87953V4.68275L6.66667 6.02144V11.992C6.50667 13.1299 5.50667 14 4.33333 14C3.04667 14 2 12.9558 2 11.6573C2 10.3655 3.04667 9.3146 4.33333 9.3146C4.69333 9.3146 5.02667 9.40831 5.33333 9.54887V4.01341C5.33333 3.64527 5.62667 3.40431 5.99333 3.34407Z" fill="#303030"/>
    </svg>`;
    } else if (pdfContentTypes.includes(file.type)) {
        fileTypeHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <g clip-path="url(#clip0_189_2849)">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 0C0.671573 0 0 0.671573 0 1.5V14.5C0 15.3284 0.671573 16 1.5 16H14.5C15.3284 16 16 15.3284 16 14.5V1.5C16 0.671573 15.3284 0 14.5 0H1.5ZM3.40039 5.98525V9.98525H4.19706V8.47659H4.71639C5.07706 8.47659 5.35239 8.45725 5.54239 8.41925C5.69147 8.38416 5.83202 8.31956 5.95572 8.22925C6.09106 8.13392 6.20306 8.00259 6.29106 7.83592C6.37906 7.66792 6.42306 7.46125 6.42306 7.21592C6.42306 6.89725 6.34639 6.63792 6.19439 6.43725C6.04172 6.23592 5.85239 6.10525 5.62572 6.04525C5.47906 6.00525 5.16306 5.98525 4.67906 5.98525H3.40039ZM4.19706 7.79725V6.66192L4.58172 6.66259C4.86906 6.66259 5.06039 6.67125 5.15506 6.68925C5.27887 6.71001 5.39182 6.77261 5.47506 6.86659C5.56132 6.96623 5.60659 7.09488 5.60172 7.22659C5.60543 7.33603 5.57476 7.44389 5.51402 7.53501C5.45328 7.62612 5.36551 7.69592 5.26306 7.73459C5.15706 7.77592 4.94706 7.79725 4.63306 7.79725H4.19706ZM8.34639 5.98525H6.89039V9.98525H8.38972C8.68372 9.98525 8.91906 9.95725 9.09439 9.90125C9.32972 9.82459 9.51639 9.71792 9.65439 9.58125C9.83706 9.40125 9.97839 9.16592 10.0771 8.87459C10.1584 8.63659 10.1984 8.35259 10.1984 8.02325C10.1984 7.64859 10.1557 7.33392 10.0691 7.07792C9.98306 6.82259 9.85706 6.60659 9.69239 6.42992C9.52706 6.25392 9.32906 6.13059 9.09772 6.06192C8.92506 6.01059 8.67506 5.98525 8.34639 5.98525ZM7.68706 9.31125V6.66192H8.04572C8.36972 6.66192 8.58772 6.67459 8.69906 6.69992C8.83714 6.7272 8.96483 6.79255 9.06772 6.88859C9.17416 6.99696 9.25204 7.13004 9.29439 7.27592C9.34772 7.44125 9.37439 7.67859 9.37439 7.98792C9.37439 8.29725 9.34772 8.54125 9.29372 8.72059C9.24039 8.89992 9.17039 9.02859 9.08506 9.10659C9.00039 9.18525 8.89306 9.23992 8.76372 9.27325C8.66506 9.29859 8.50439 9.31125 8.28172 9.31125H7.68706ZM10.6957 5.98525V9.98525H11.4924V8.28525H13.1391V7.60859H11.4924V6.66192H13.4004V5.98525H10.6957Z" fill="#F03D04"/>
      </g>
      <defs>
      <clipPath id="clip0_189_2849">
      <rect width="16" height="16" fill="white"/>
      </clipPath>
      </defs>
    </svg>`;
    } else if (codeContentTypes.includes(file.type)) {
        fileTypeHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <g clip-path="url(#clip0_189_3849)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 0C0.671573 0 0 0.671573 0 1.5V14.5C0 15.3284 0.671573 16 1.5 16H14.5C15.3284 16 16 15.3284 16 14.5V1.5C16 0.671573 15.3284 0 14.5 0H1.5ZM10.6667 10.1L13 8L10.6667 5.9L10.1967 6.323L12.0567 8L10.1967 9.677L10.6667 10.1ZM5.33333 5.9L3 8L5.33333 10.1L5.80333 9.677L3.94333 8L5.80333 6.323L5.33333 5.9ZM8.54667 5L6.80667 10.8452L7.45067 11L9.19067 5.1551L8.54667 5Z" fill="#3E3E3E"/>
      </g>
      <defs>
        <clipPath id="clip0_189_3849">
          <rect width="16" height="16" fill="white"/>
        </clipPath>
      </defs>
    </svg>`;
    } else {
        fileTypeHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <g clip-path="url(#clip0_189_3844)">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 0C0.671573 0 0 0.671573 0 1.5V14.5C0 15.3284 0.671573 16 1.5 16H14.5C15.3284 16 16 15.3284 16 14.5V1.5C16 0.671573 15.3284 0 14.5 0H1.5ZM8.41602 4.4585V6.75016H10.7077L8.41602 4.4585ZM4.66602 4.66683C4.66602 4.20433 5.03685 3.8335 5.49935 3.8335H8.83268L11.3327 6.3335V11.3335C11.3327 11.5545 11.2449 11.7665 11.0886 11.9228C10.9323 12.079 10.7204 12.1668 10.4993 12.1668H5.49935C5.27834 12.1668 5.06637 12.079 4.91009 11.9228C4.75381 11.7665 4.66602 11.5545 4.66602 11.3335V4.66683Z" fill="#FEAC1D"/>
      </g>
      <defs>
      <clipPath id="clip0_189_3844">
      <rect width="16" height="16" fill="white"/>
      </clipPath>
      </defs>
    </svg>`;
    }
    return svgNode(fileTypeHTML);
}

/**
 * Visitor-facing upload failure messages, keyed by the API's machine-readable error
 * code (the `error` field of the JSON body, not its `message`).
 *
 * Two rules here, both deliberate:
 *
 *   NEVER render the API's own `message`. Those are written for developers — e.g.
 *   "Invalid Content-Type. Expected multipart/form-data (with boundary)" — and can
 *   describe internals that have no business on a public page. `data-fd-id` is
 *   visible in page source, so anyone can call the endpoint; echoing back precisely
 *   why their request was rejected hands them a free diagnostic tool.
 *
 *   KEEP THEM SHORT. This renders in the file row's status slot next to a cancel
 *   button, in a widget that may sit in a container under 320px wide.
 *
 * Anything not listed falls back to GENERIC_UPLOAD_ERROR. The full error is always
 * logged to the console for the site owner.
 */
const UPLOAD_ERROR_MESSAGES = {
    FILE_TOO_LARGE: "Too large",
    NETWORK: "No connection",
    // Upload rejected because the page's domain isn't on the key's allowed list
    // (Webflow App Review finding #4). Short for the status slot; the full,
    // actionable reason — which domain to add — is in the console (line ~1495)
    // and the API's `message`, both for the site owner.
    domain_not_allowed: "Domain not allowed",
};
const GENERIC_UPLOAD_ERROR = "Upload failed";

/** Map an upload rejection to a short, safe, visitor-facing string. */
function uploadErrorMessage(error) {
    const code = error && error.code;
    return (code && UPLOAD_ERROR_MESSAGES[code]) || GENERIC_UPLOAD_ERROR;
}

/** An Error carrying a machine-readable code for uploadErrorMessage() to map. */
function taggedError(message, code) {
    const err = new Error(message);
    err.code = code;
    return err;
}

/**
 * Show a validation error inside the widget.
 *
 * Replaces the blocking `alert()` dialogs: those halted the page, could not be
 * styled or dismissed, and stacked up one modal per rejected file when someone
 * dropped a batch.
 *
 * `role="alert"` + `aria-live="assertive"` make screen readers announce it, which
 * the native dialog did for free and a silent <div> would not. The filename goes in
 * via textContent — same reason as everywhere else in this file.
 */
function showWidgetError(anchor, filename, message) {
    if (!anchor) return;

    let box = anchor.querySelector("fdx-error");
    if (!box) {
        box = document.createElement("fdx-error");
        box.setAttribute("role", "alert");
        box.setAttribute("aria-live", "assertive");
        anchor.appendChild(box);
    }

    const row = fdxEl("div", { "fdx-error-row": "" });

    const text = fdxEl("div", { "fdx-error-text": "" });
    const nameEl = fdxEl("span", { "fdx-error-file": "" });
    nameEl.textContent = filename;
    // The name is ellipsised at narrow widths; title= gives the full value back on
    // hover. Supplementary only — the reason is always visible, and the untruncated
    // name is still in the DOM for assistive tech.
    nameEl.title = filename;
    const msgEl = fdxEl("span", { "fdx-error-message": "" });
    msgEl.textContent = message;
    text.append(nameEl, msgEl);

    const dismiss = fdxEl("button", {
        type: "button",
        "fdx-error-dismiss": "",
        "aria-label": `Dismiss error for ${filename}`
    });
    dismiss.textContent = "✕";
    dismiss.addEventListener("click", () => {
        row.remove();
        if (!box.querySelector("[fdx-error-row]")) box.remove();
    });

    row.append(text, dismiss);
    box.appendChild(row);
}

/** Drop any errors from a previous selection so they don't pile up. */
function clearWidgetErrors(anchor) {
    if (!anchor) return;
    anchor.querySelectorAll("fdx-error").forEach((box) => box.remove());
}

/**
 * Create an element and set attributes via setAttribute.
 *
 * Attribute values are never parsed as markup, so a filename cannot escape into one
 * the way it could inside an interpolated HTML string.
 */
function fdxEl(tag, attrs) {
    const node = document.createElement(tag);
    for (const name in attrs || {}) {
        node.setAttribute(name, attrs[name]);
    }
    return node;
}

/**
 * Parse a fixed SVG string into a DOM node.
 *
 * Only ever called with the literal markup above — never with anything derived from
 * a file, a URL, or any other input. DOMParser builds an inert tree (no scripts run,
 * no resources load), which keeps the icons out of innerHTML entirely.
 */
function svgNode(markup) {
    const doc = new DOMParser().parseFromString(markup, "image/svg+xml");
    const svg = doc.documentElement;
    // A malformed string yields a <parsererror> document rather than throwing.
    if (!svg || svg.nodeName === "parsererror") {
        return document.createDocumentFragment();
    }
    return document.importNode(svg, true);
}

function handleFileSelection(files, element, isMultiple) {
    // Find the closest fdx-container or define logic to find the correct container
    let container = element.closest("fdx-container") || document.body; // Adjust based on your structure
    const folderId = element.getAttribute('fd-folder');
    if (!isMultiple) {
        // Clear previous previews if not multiple
        container
            .querySelectorAll("fdx-file-wrapper")
            .forEach((wrapper) => wrapper.remove());
    }

    Array.from(files).forEach((file) => {
        // Built with DOM APIs rather than an interpolated innerHTML string. The old
        // template dropped `${file.name}` straight into markup, so selecting a file
        // named  <img src=x onerror=...>  executed it on the customer's published page
        // — before any upload was authorised. textContent cannot be parsed as markup.
        const filePreview = document.createElement("fdx-file-wrapper");
        const fdxFile = fdxEl("fdx-file");

        const details = fdxEl("div", { "fdx-file-details": "" });
        const icon = fdxEl("div", { "fdx-file-icon": "" });
        const iconNode = getFileTypeIcon(file);
        icon.appendChild(iconNode);

        // getFileTypeIcon mints a blob URL for image previews. Hold on to it so cancel
        // can revoke it — otherwise it leaks for the lifetime of the page.
        const filePreviewUrl = iconNode.tagName === "IMG" ? iconNode.src : null;

        const title = fdxEl("div", { "fdx-file-title": "" });
        const nameEl = fdxEl("div", { "font-weight-medium": "", "fdx-file-text": "" });
        nameEl.textContent = file.name;
        const sizeEl = fdxEl("div", { "u-text-grey": "" });
        sizeEl.textContent = formatSize(file.size);
        title.append(nameEl, fdxEl("div", { "fdx-divider": "" }), sizeEl);

        details.append(icon, title);

        const progress = fdxEl("div", {
            "fdx-progress": "",
            role: "progressbar",
            "aria-valuenow": "0",
            "aria-valuemin": "0",
            "aria-valuemax": "100",
            "aria-label": "Upload progress"
        });

        const action = fdxEl("div", { "fdx-action": "" });
        const status = fdxEl("div", { "fdx-file-status": "" });
        status.textContent = "Uploading 0%";
        const cancel = fdxEl("a", {
            "aria-label": "cancel upload",
            role: "button",
            "fdx-cancel-upload": ""
        });
        cancel.appendChild(
            svgNode(`<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M5.63086 14.3692L10 10L14.3692 14.3692M14.3692 5.63086L9.99919 10L5.63086 5.63086" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"></path>
                        </svg>`)
        );
        action.append(status, cancel);

        fdxFile.append(details, progress, action);
        filePreview.appendChild(fdxFile);

        // Append the file preview to the fdx-container
        if (isMultiple) {
            // Append file preview for multiple uploads
            container.appendChild(filePreview);
        } else {
            // Replace existing preview for single uploads
            const existingPreview = container.querySelector("fdx-file-wrapper");
            if (existingPreview) {
                existingPreview.remove();
            }
            container.appendChild(filePreview);
        }

        // Start file upload with callbacks for progress and completion
        uploadFile(
            file,
            folderId,
            // Progress callback
            (progress) => {
                const fdxProgress = filePreview.querySelector("[fdx-progress]");
                const fdxStatus = filePreview.querySelector("[fdx-file-status]");
                fdxProgress.style.width = `${progress}%`;
                fdxStatus.textContent = `Uploading ${progress}%`;
            },
            // Completion callback
            (success, fileUrl, error) => {
                const fdxProgress = filePreview.querySelector("[fdx-progress]");
                const fdxStatus = filePreview.querySelector("[fdx-file-status]");
                if (success) {
                    fdxProgress.style.display = "none"; // You might want to hide or remove the progress bar
                    fdxStatus.replaceChildren(
                        svgNode(`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M6.99999 1.1665C3.79166 1.1665 1.16666 3.7915 1.16666 6.99984C1.16666 10.2082 3.79166 12.8332 6.99999 12.8332C10.2083 12.8332 12.8333 10.2082 12.8333 6.99984C12.8333 3.7915 10.2083 1.1665 6.99999 1.1665ZM6.99999 11.6665C4.42749 11.6665 2.33332 9.57234 2.33332 6.99984C2.33332 4.42734 4.42749 2.33317 6.99999 2.33317C9.57249 2.33317 11.6667 4.42734 11.6667 6.99984C11.6667 9.57234 9.57249 11.6665 6.99999 11.6665ZM9.67749 4.4215L5.83332 8.26567L4.32249 6.76067L3.49999 7.58317L5.83332 9.9165L10.5 5.24984L9.67749 4.4215Z" fill="#008E39"/>
                </svg>`),
                        document.createTextNode(" Uploaded")
                    );
                    fdxStatus.setAttribute("active", "");

                    // Logic to update or use the existing input based on 'fd-input'
                    const inputSelector = container.getAttribute("fd-input");
                    let inputElement;

                    if (inputSelector) {
                        if (
                            inputSelector.startsWith(".") ||
                            inputSelector.startsWith("#") ||
                            inputSelector.startsWith("[")
                        ) {
                            inputElement = document.querySelector(inputSelector);
                        } else {
                            // 'fd-input' is assumed to be a name
                            inputElement = container.querySelector(
                                `input[name="${inputSelector}"]`
                            );
                            if (!inputElement) {
                                // If no input matches the name, create a new hidden input
                                // (finding #6: not one that duplicates or is sensitive).
                                inputElement = createOwnedField(inputSelector, container.closest("form"));
                                if (inputElement) container.appendChild(inputElement);
                            }
                        }

                        // No usable field (not found, or refused under finding #6, which
                        // has already warned): skip the write but still report the upload.
                        if (inputElement) {
                            updateHiddenInput(inputElement, fileUrl, isMultiple); // Use the extracted URL
                        } else {
                            console.error("No field for 'fd-input':", inputSelector);
                        }
                        filePreview.dataset.fileUrl = fileUrl;
                    }
                    emitWidgetEvent(container, "fdupload", {
                        url: fileUrl, name: file.name, size: file.size, type: file.type
                    });
                } else {
                    // A cancelled upload is the visitor's own doing, not a failure —
                    // the row is being removed anyway.
                    if (error && error.code === "CANCELLED") return;
                    fdxStatus.textContent = uploadErrorMessage(error);
                    emitWidgetEvent(container, "fderror", {
                        name: file.name, code: (error && error.code) || null, message: uploadErrorMessage(error)
                    });
                }
            }
        ).catch((error) => {
            // uploadFile already logged the detail; this is the UI half.
            // A cancelled upload is the visitor's own action — leave the row alone.
            if (error && error.code === "CANCELLED") return;
            const fdxStatus = filePreview.querySelector("[fdx-file-status]");
            fdxStatus.replaceChildren(
                svgNode(`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"> <path d="M6.41666 8.74984H7.58332V9.9165H6.41666V8.74984ZM6.41666 4.08317H7.58332V7.58317H6.41666V4.08317ZM6.99999 1.1665C3.77416 1.1665 1.16666 3.7915 1.16666 6.99984C1.16666 8.54693 1.78124 10.0307 2.8752 11.1246C3.41688 11.6663 4.05994 12.096 4.76767 12.3891C5.4754 12.6823 6.23395 12.8332 6.99999 12.8332C8.54709 12.8332 10.0308 12.2186 11.1248 11.1246C12.2187 10.0307 12.8333 8.54693 12.8333 6.99984C12.8333 6.23379 12.6824 5.47525 12.3893 4.76752C12.0961 4.05978 11.6665 3.41672 11.1248 2.87505C10.5831 2.33337 9.94004 1.90369 9.23231 1.61054C8.52458 1.31739 7.76603 1.1665 6.99999 1.1665ZM6.99999 11.6665C5.76231 11.6665 4.57533 11.1748 3.70016 10.2997C2.82499 9.4245 2.33332 8.23751 2.33332 6.99984C2.33332 5.76216 2.82499 4.57518 3.70016 3.70001C4.57533 2.82484 5.76231 2.33317 6.99999 2.33317C8.23767 2.33317 9.42465 2.82484 10.2998 3.70001C11.175 4.57518 11.6667 5.76216 11.6667 6.99984C11.6667 8.23751 11.175 9.4245 10.2998 10.2997C9.42465 11.1748 8.23767 11.6665 6.99999 11.6665Z" fill="#FA5E54"/>
                  </svg>`),
                document.createTextNode(` ${uploadErrorMessage(error)}`)
            );
            fdxStatus.setAttribute("error", "");
        });

        let inputElement = container.querySelector(
            `input[name="${element.getAttribute("fd-input")}"]`
        );
        if (!inputElement) {
            inputElement = document.querySelector(element.getAttribute("fd-input")); // Try to select using the selector
        }

        filePreview
            .querySelector("[fdx-cancel-upload]")
            .addEventListener("click", () => {
                // Abort EVERY in-flight request (multipart runs several at once) and
                // clean up the partial upload server-side.
                if (typeof file.abortAll === 'function') file.abortAll();

                // Read the URL off the row BEFORE removing it, and detach the form input
                // BEFORE any cleanup that could throw. Ordering matters: this used to
                // remove the row, then throw on a revoke, and never reach the input —
                // leaving the form holding a URL for a file the visitor had cancelled.
                const fileUrlToRemove = filePreview.dataset.fileUrl;

                filePreview.remove();
                if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);

                // Handle differently for single and multiple uploads
                if (isMultiple) {
                    if (fileUrlToRemove) {
                        // Assuming hiddenInput is properly scoped to be accessible here
                        updateHiddenInput(
                            inputElement,
                            fileUrlToRemove,
                            isMultiple,
                            "remove"
                        ); // Remove the URL from the hidden input for multiple
                    }
                } else {
                    // For single file uploads, simply clear the hidden input
                    if (inputElement) {
                        setInputValue(inputElement, ""); // Clear the value for single file uploads
                    }
                    const uploadButton = container.querySelector("fdx-upload");
                    if (uploadButton) uploadButton.style.display = ""; // Make the upload button visible again for single uploads
                }
                emitWidgetEvent(container, "fdremove", { url: fileUrlToRemove || null, name: file.name });
            });
    });

    // Adjust visibility of fdx-upload based on isMultiple
    const uploadButton = container.querySelector("fdx-upload");
    if (uploadButton && files.length && !isMultiple) {
        uploadButton.style.display = "none";
    }
}

// App Review finding #6. fd-input can name any field on the page, and whatever the
// widget writes there is what the form submits, to Webflow Forms or wherever the form
// posts. So the widget refuses a target that is a password, email or other non-text
// field, is marked or named as a password, security, captcha or payment field, or sits
// in a payment or Webflow checkout form. It leaves that field alone and logs why.
// A form that merely contains a password field (a sign-up form with a profile photo)
// is fine: the widget only ever touches its own target field.
const FD_OWNED = "__fdOwned"; // set on fields the widget created or has already filled

const SENSITIVE_AUTOCOMPLETE =
    /(^|\s)(current-password|new-password|one-time-code|username|webauthn|cc-[a-z-]+)(\s|$)/i;
const PAYMENT_AUTOCOMPLETE = /(^|\s)cc-[a-z-]+(\s|$)/i;
// Whole words, after splitting the name/id on separators and camelCase.
const SECURITY_WORDS = new Set([
    "password", "passwd", "passcode", "pwd", "otp", "totp", "mfa", "2fa",
    "csrf", "xsrf", "nonce", "secret", "token", "authenticity",
]);
const PAYMENT_WORDS = new Set([
    "cvv", "cvc", "csc", "iban", "ssn", "creditcard", "cardnumber", "cardnum",
    "ccnumber", "ccnum", "cardholder",
]);
const PAYMENT_PHRASES =
    /\b(card|cc|credit) (number|num|no|exp|expiry|expiration|code|csc|cvc|cvv|holder)\b|\bsecurity code\b|\bcredit card\b|\bverification value\b/;
// Substrings that stay sensitive inside compound names (csrfmiddlewaretoken).
const SENSITIVE_FRAGMENTS = ["password", "passwd", "csrf", "xsrf"];
// Captcha response fields that Webflow forms (reCAPTCHA) and others add to a form.
const CAPTCHA_NAMES = new Set(["g-recaptcha-response", "h-captcha-response", "cf-turnstile-response"]);
// What the widget itself writes: one https URL, or several as <url> <url>.
const UPLOAD_URLS = /^\s*(<?https:\/\/[^\s<>]+>?\s*)+$/;
const WRITABLE_TYPES = ["text", "url", "search", "hidden"];
const CHECKOUT_SELECTOR =
    '[class*="w-commerce"][class*="checkout"], [data-node-type*="checkout"]';

function fieldWords(value) {
    return String(value || "")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean);
}

/** True when a field name or id reads like a payment field. */
function isPaymentName(value) {
    const words = fieldWords(value);
    return words.some((w) => PAYMENT_WORDS.has(w)) || PAYMENT_PHRASES.test(words.join(" "));
}

/** True when a field name or id reads like a password, security, captcha or payment field. */
function isSensitiveName(value) {
    if (!value) return false;
    const lower = String(value).toLowerCase();
    if (CAPTCHA_NAMES.has(lower)) return true;
    if (SENSITIVE_FRAGMENTS.some((f) => lower.includes(f))) return true;
    return fieldWords(value).some((w) => SECURITY_WORDS.has(w)) || isPaymentName(value);
}

function isPaymentField(el) {
    return (
        PAYMENT_AUTOCOMPLETE.test((el.getAttribute && el.getAttribute("autocomplete")) || "") ||
        isPaymentName(el.getAttribute && el.getAttribute("name")) ||
        isPaymentName(el.id)
    );
}

/** Why the widget must not touch this field, or null when it may write to it. */
function forbiddenTargetReason(el) {
    if (!el || (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA")) {
        return "it is not a text field";
    }
    const type = el.tagName === "INPUT" ? (el.getAttribute("type") || "text").trim().toLowerCase() : "textarea";
    if (type !== "textarea" && !WRITABLE_TYPES.includes(type)) return `it is a ${type} field`;
    // Attribute checks come before anything that reads the field's value.
    if (SENSITIVE_AUTOCOMPLETE.test(el.getAttribute("autocomplete") || "")) {
        return "it is marked as a password, sign-in or payment field";
    }
    if (isSensitiveName(el.getAttribute("name")) || isSensitiveName(el.id)) {
        return "its name looks like a password, security, captcha or payment field";
    }
    const form = el.form || el.closest("form");
    if (form && Array.prototype.some.call(form.elements, isPaymentField)) {
        return "its form has payment fields";
    }
    if (el.closest(CHECKOUT_SELECTOR)) return "it is in a Webflow checkout form";
    // Only a hidden field whose name passed the checks above has its value read, to
    // tell an empty field or a previous upload URL from some other value.
    if (type === "hidden" && !el[FD_OWNED] && el.value && !UPLOAD_URLS.test(el.value)) {
        return "it is a hidden field that already holds another value";
    }
    return null;
}

/**
 * Why the widget must not create a hidden field called `name` in `form`, or null.
 * A second field with an existing name (say "password") changes what the form
 * submits, because many backends keep the last value sent under a name.
 */
function forbiddenNewFieldReason(name, form) {
    if (isSensitiveName(name)) return "the name looks like a password, security, captcha or payment field";
    const existing = form && form.elements && form.elements.namedItem(name);
    if (existing && !existing[FD_OWNED]) return "the form already has a field with that name";
    return null;
}

const warnedTargets = new WeakSet();

/** True when the widget may write to `el`. Warns once per refused field. */
function canWriteTarget(el) {
    const reason = forbiddenTargetReason(el);
    if (!reason) return true;
    if (el && !warnedTargets.has(el)) {
        warnedTargets.add(el);
        console.warn(
            `Flowdrive: not writing the upload URL to this field because ${reason}. ` +
            "Point fd-input at a dedicated text or hidden field.",
            el
        );
    }
    return false;
}

/** A new hidden field for the upload URL, or null (with a warning) when it must not exist. */
function createOwnedField(name, form) {
    const reason = forbiddenNewFieldReason(name, form);
    if (reason) {
        console.warn(
            `Flowdrive: not adding a hidden field named "${name}" because ${reason}. ` +
            "Set fd-input to another name.",
        );
        return null;
    }
    const field = document.createElement("input");
    field.type = "hidden";
    field.name = name;
    field[FD_OWNED] = true;
    return field;
}

// Write a form value so frameworks see it. A plain `input.value = x` fires no event,
// so Svelte's bind:value and React's onChange never hear about the upload. React also
// tracks the value it last set and ignores writes through the instance property, so
// the write goes through the prototype's native setter, then input + change fire.
function setInputValue(inputElement, value) {
    if (!inputElement || !canWriteTarget(inputElement)) return;
    const proto = Object.getPrototypeOf(inputElement);
    const desc = proto && Object.getOwnPropertyDescriptor(proto, "value");
    if (desc && desc.set) desc.set.call(inputElement, value);
    else inputElement.value = value;
    inputElement[FD_OWNED] = true;
    inputElement.dispatchEvent(new Event("input", { bubbles: true }));
    inputElement.dispatchEvent(new Event("change", { bubbles: true }));
}

// Public events, fired on the widget and bubbling, so any stack can react without
// reading hidden inputs:  fdupload {url,name,size,type} · fdremove {url,name} ·
// fderror {name,code,message}.  Svelte: onfdupload={…}; React or plain JS:
// el.addEventListener('fdupload', …).
function emitWidgetEvent(target, name, detail) {
    if (!target) return;
    target.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
}

function updateHiddenInput(inputElement, fileUrl, isMultiple, action = "add") {
    // Refused fields are never written, and their value is not read here (finding #6).
    if (!canWriteTarget(inputElement)) return;
    // Split by angle brackets and clean up, handling malformed URLs
    let existingUrls = [];

    if (inputElement.value) {
        // First split by newlines, then clean up each URL
        existingUrls = inputElement.value
            .split(/[\n>]/) // Split by newline or closing bracket
            .map(url => url.trim())
            .map(url => url.replace(/^</, '')) // Remove leading <
            .filter(url => url && url.startsWith('https://')); // Only keep valid URLs
    }

    // Ensure URL is properly encoded
    const encodedUrl = encodeURI(fileUrl);

    if (action === "add") {
        if (isMultiple) {
            existingUrls.push(encodedUrl);
        } else {
            existingUrls = [encodedUrl];
        }
    } else if (action === "remove" && isMultiple) {
        existingUrls = existingUrls.filter((url) => url !== encodedUrl);
    }

    // Format URLs differently based on whether it's multiple or single
    if (isMultiple) {
        // For multiple files, wrap each URL in angle brackets and join with spaces
        setInputValue(inputElement, existingUrls
            .map(url => `<${url}>`)
            .join(' '));
    } else {
        // For single file, just use the URL directly
        setInputValue(inputElement, existingUrls[0] || '');
    }
}

function parseSize(sizeStr) {
    const units = {
        kb: 1024,
        mb: 1024 * 1024,
        gb: 1024 * 1024 * 1024,
    };
    const regex = /^(\d+)(kb|mb|gb)$/i;
    const matches = sizeStr.match(regex);
    if (matches) {
        const size = parseInt(matches[1], 10);
        const unit = matches[2].toLowerCase();
        return size * (units[unit] || 0);
    }
    return Infinity; // No limit if there's a parsing error or no value provided
}

//Upload file
async function uploadFile(file, folderId, onProgress, onComplete) {
    const BASE_URL = 'https://api.tryflowdrive.com/v1';
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB in bytes
    const MAX_RETRIES = 3;
    const MAX_CONCURRENT_UPLOADS = 3;

    // Track EVERY in-flight request so cancellation aborts all of them. Multipart
    // runs up to MAX_CONCURRENT_UPLOADS parts in parallel; the old code kept a single
    // `file.xhr` that each part overwrote, so cancelling aborted only the most recent
    // and left the other parts uploading (App Review finding 10).
    file._xhrs = new Set();
    file._cancelled = false;
    file._multipart = null; // { key, uploadId } once a multipart upload is initiated

    function trackXhr(xhr) {
        file._xhrs.add(xhr);
        // loadend fires on success, error, AND abort — so the set self-cleans.
        xhr.addEventListener('loadend', function () { file._xhrs.delete(xhr); });
        return xhr;
    }

    // Abort every active request, stop new batches from launching, and tell the
    // server to discard a partial multipart upload so its parts don't linger.
    file.abortAll = function () {
        file._cancelled = true;
        file._xhrs.forEach(function (xhr) {
            try { if (xhr.readyState !== 4) xhr.abort(); } catch (e) {}
        });
        file._xhrs.clear();

        var mp = file._multipart;
        file._multipart = null;
        if (mp) {
            // Best-effort server cleanup; keepalive lets it survive the page unloading.
            fetch(BASE_URL + '/files/upload', {
                method: 'DELETE',
                headers: { 'x-api-key': apiKey, 'Content-Type': 'application/json' },
                body: JSON.stringify({ key: mp.key, uploadId: mp.uploadId }),
                keepalive: true
            }).catch(function () {});
        }
    };

    try {
        if (file.size <= CHUNK_SIZE) {
            // Direct upload for files <= 5MB
            return await directUpload(file, folderId, onProgress);
        } else {
            // Multipart upload for files > 5MB
            return await multipartUpload(file, folderId, onProgress);
        }
    } catch (error) {
        // A cancel is not a failure. 1.1.8 still logged
        // "Flowdrive upload failed: Error: Upload cancelled" from here whenever a
        // visitor clicked ✕, which reads as a bug to the site owner watching their
        // console — and this is the finding-#7 area, so it is the last place that
        // should look broken. Every call site already discriminates on
        // `error.code === "CANCELLED"`; this one did not.
        //
        // Full detail still goes to the console for a REAL failure; the UI only ever
        // renders the short mapped string from uploadErrorMessage().
        if (!(error && error.code === 'CANCELLED')) {
            console.error('Flowdrive upload failed:', error);
        }
        onComplete(false, null, error);
        throw error;
    }

    // Helper function for direct upload using XHR
    function directUpload(file, folderId, onProgress) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            trackXhr(xhr);

            const formData = new FormData();
            formData.append('file', file);
            if (folderId) {
                formData.append('folderId', folderId);
            }

            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    onProgress(progress);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.response);
                    onComplete(true, response.file_url);
                    resolve(response);
                } else {
                    // The API answers with { error: 'MACHINE_CODE', message: '<for
                    // developers>' }. Carry the code on the Error so the UI can map it
                    // to something a visitor can act on; the message stays for the log.
                    let body = {};
                    try {
                        body = xhr.response ? JSON.parse(xhr.response) : {};
                    } catch {
                        // Non-JSON body (proxy error page, truncated response) — the
                        // status is all we have.
                    }
                    const err = new Error(body.message || `Upload failed (HTTP ${xhr.status})`);
                    err.code = body.error;
                    err.status = xhr.status;
                    // onComplete is invoked by uploadFile's catch — calling it here too
                    // fired the completion callback twice for every failure.
                    reject(err);
                }
            });

            xhr.addEventListener('error', () => {
                const err = new Error('Network error');
                err.code = 'NETWORK';
                reject(err);
            });

            xhr.addEventListener('abort', () => {
                const err = new Error('Upload cancelled');
                err.code = 'CANCELLED';
                reject(err);
            });

            xhr.open('POST', `${BASE_URL}/files/upload`);
            xhr.setRequestHeader('x-api-key', apiKey);
            xhr.send(formData);
        });
    }

    // Helper function for multipart upload using XHR
    async function multipartUpload(file, folderId, onProgress) {
        // Step 1: Initialize multipart upload using XHR
        const initResult = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            trackXhr(xhr);

            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    resolve(JSON.parse(xhr.response));
                } else {
                    // Carry the API's machine-readable code so uploadErrorMessage() can
                    // map it (e.g. domain_not_allowed -> "Domain not allowed"), matching
                    // directUpload. Without this, every multipart failure showed the
                    // generic "Upload failed" even when the API said exactly why.
                    let body = {};
                    try {
                        body = xhr.response ? JSON.parse(xhr.response) : {};
                    } catch {
                        // Non-JSON body — status is all we have.
                    }
                    const err = new Error(body.message || `Failed to initialize upload (HTTP ${xhr.status})`);
                    err.code = body.error;
                    err.status = xhr.status;
                    reject(err);
                }
            });

            xhr.addEventListener('error', () => reject(taggedError('Network error', 'NETWORK')));
            xhr.open('POST', `${BASE_URL}/files/upload`);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.setRequestHeader('x-api-key', apiKey);
            xhr.send(JSON.stringify({
                fileName: file.name,
                fileSize: file.size,
                contentType: file.type,
                folderId: folderId
            }));
        });

        const { uploadId, key } = initResult;
        // Remember it so a cancel can clean up the partial upload server-side.
        file._multipart = { key: key, uploadId: uploadId };
        const chunks = Math.ceil(file.size / CHUNK_SIZE);

        // Create arrays to track progress and uploaded bytes of each chunk
        const chunkProgress = new Array(chunks).fill(0);
        const chunkSizes = new Array(chunks).fill(0);

        // Calculate the size of each chunk
        for (let i = 0; i < chunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            chunkSizes[i] = end - start;
        }

        // Function to calculate total progress based on actual bytes uploaded
        function calculateTotalProgress() {
            let totalUploaded = 0;
            let totalSize = 0;

            for (let i = 0; i < chunks; i++) {
                totalUploaded += (chunkSizes[i] * chunkProgress[i] / 100);
                totalSize += chunkSizes[i];
            }

            return Math.round((totalUploaded / totalSize) * 99); // Max 99% until final PATCH
        }

        // Function to upload a single part using XHR
        function uploadPart(partNumber) {
            return new Promise((resolve, reject) => {
                const start = (partNumber - 1) * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);

                const xhr = new XMLHttpRequest();
                trackXhr(xhr);

                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        // Calculate real-time progress for this chunk
                        const partProgress = (event.loaded / event.total) * 100;
                        chunkProgress[partNumber - 1] = partProgress;
                        onProgress(calculateTotalProgress());
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status === 200) {
                        const result = JSON.parse(xhr.response);
                        // Mark chunk as fully uploaded
                        chunkProgress[partNumber - 1] = 100;
                        onProgress(calculateTotalProgress());
                        resolve({
                            etag: result.etag,
                            partNumber: partNumber
                        });
                    } else {
                        reject(new Error('Part upload failed'));
                    }
                });

                xhr.addEventListener('error', () => reject(taggedError('Network error', 'NETWORK')));
                xhr.addEventListener('abort', () => reject(taggedError('Upload cancelled', 'CANCELLED')));

                xhr.open('PUT', `${BASE_URL}/files/upload?key=${encodeURIComponent(key)}&uploadId=${uploadId}&partNumber=${partNumber}`);
                xhr.setRequestHeader('Content-Type', 'application/octet-stream');
                xhr.setRequestHeader('x-api-key', apiKey);
                xhr.send(chunk);
            });
        }

        // Upload parts with retry logic
        async function uploadPartWithRetry(partNumber) {
            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                // Cancelled before we (re)launch this part — never start a replacement
                // request. Covers the race where the cancel lands while this part is
                // sitting in the backoff delay below: when the timer fires we come back
                // here and stop, instead of opening a fresh PUT (App Review finding 5).
                if (file._cancelled) throw taggedError('Upload cancelled', 'CANCELLED');
                try {
                    return await uploadPart(partNumber);
                } catch (error) {
                    // A cancel aborts the in-flight part (rejects CANCELLED) AND sets
                    // file._cancelled. Either signal means stop permanently — no progress
                    // reset, no backoff, no retry. Without this the retry loop relaunched
                    // every aborted part after the delay, racing the DELETE cleanup that
                    // abortAll() already fired.
                    if (file._cancelled || (error && error.code === 'CANCELLED')) throw error;

                    // Reset progress for this chunk on a real (retryable) error
                    chunkProgress[partNumber - 1] = 0;
                    onProgress(calculateTotalProgress());

                    if (attempt === MAX_RETRIES - 1) throw error;
                    await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
                }
            }
        }

        // Upload parts in parallel with limited concurrency
        const partNumbers = Array.from({ length: chunks }, (_, i) => i + 1);
        const results = [];

        for (let i = 0; i < partNumbers.length; i += MAX_CONCURRENT_UPLOADS) {
            // If the user cancelled, stop launching new batches. abortAll() already
            // aborted the in-flight ones and fired the server cleanup.
            if (file._cancelled) {
                throw taggedError('Upload cancelled', 'CANCELLED');
            }
            const batch = partNumbers.slice(i, i + MAX_CONCURRENT_UPLOADS);
            const batchResults = await Promise.all(batch.map(uploadPartWithRetry));
            results.push(...batchResults);
        }

        // All parts uploaded, show 99% while completing
        onProgress(99);

        // Step 3: Complete multipart upload
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            trackXhr(xhr);

            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.response);
                    // Completed successfully — there's no partial upload to clean up.
                    file._multipart = null;
                    // Only now show 100%
                    onProgress(100);
                    onComplete(true, response.file_url);
                    resolve(response);
                } else {
                    // uploadFile's catch calls onComplete — doing it here too fired the
                    // completion callback twice for every failure.
                    let body = {};
                    try {
                        body = xhr.response ? JSON.parse(xhr.response) : {};
                    } catch { /* non-JSON body; status is all we have */ }
                    const err = new Error(body.message || `Failed to complete upload (HTTP ${xhr.status})`);
                    err.code = body.error;
                    err.status = xhr.status;
                    reject(err);
                }
            });

            xhr.addEventListener('error', () => {
                const err = new Error('Network error');
                err.code = 'NETWORK';
                reject(err);
            });

            xhr.open('PATCH', `${BASE_URL}/files/upload`);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.setRequestHeader('x-api-key', apiKey);
            xhr.send(JSON.stringify({
                key,
                uploadId,
                parts: results,
                contentType: file.type,
                folderId: folderId
            }));
        });
    }
}

// ─── end single-initialization guard / module scope ─────────────────────────
// The body above is deliberately left at its original indentation: re-indenting
// 1,400 lines to wrap them would have buried the actual change in whitespace and
// made the diff unreviewable. Minification collapses it either way.
})();